"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.processUnexpectedErrors = exports.matchesExpected = exports.AUTH_EXPECTED_ERRORS = void 0;
const util_1 = require("@app/util");
const error_handler_1 = require("@app/util/error-handler");
const error_type_1 = __importDefault(require("@app/const/error-type"));
/**
 * Failures the auth screens treat as part of the flow rather than as breakage. Pass
 * them to a request as `expectedErrors` and it will skip `processErrors` for them —
 * the screen is expected to render them inline in the card instead.
 *
 * Values are either a `UtilError.error.type` (frontend-side classification) or a
 * backend error code, both of which `matchesExpected` understands.
 */
exports.AUTH_EXPECTED_ERRORS = Object.freeze({
    BAD_LOGIN: error_type_1.default.API_ERR_BAD_LOGIN,
    EMAIL_NOT_VERIFIED: 'auth.email_not_verified',
    LOGIN_BLOCKED: 'auth.login_blocked',
    // /verify-email: the link is expired, already spent, or the account is verified
    INVALID_TOKEN: 'auth.invalid_token',
    ALREADY_VERIFIED: 'auth.already_verified',
    // userEmailVerifyResend: 60s between sends, 5 per hour
    VERIFY_COOLDOWN: 'auth.verify_cooldown',
    VERIFY_SEND_LIMIT: 'auth.verify_send_limit',
    /**
     * `/reset-password`: the link is expired (60 min), already spent, or superseded by a
     * newer send. `userPasswordReset` throws a bare `Error('Invalid token')` with no
     * `auth.*` code, so this matches on the TYPE `_resolveError` gives that message.
     *
     * Deliberately not `eT.API_ERR_INVALID_TOKEN` — that type belongs to
     * `'Context creation failed: Invalid token'`, whose branch resets the store and
     * reloads the app (`error-handler.tsx:54`). Matching on it here would wipe the
     * session instead of rendering a card.
     */
    RESET_INVALID_TOKEN: error_type_1.default.API_ERR_INVALID_ACTIVATE,
    /**
     * `/accept-invite`: the session already holds a role on the invited workspace, so the
     * accept is a no-op rather than a failure. `userInviteAccept` reports it as
     * 'User email already exists on target workspace', which `error-handler.tsx:86` maps
     * to this type — there is no `invite.*` code for it.
     */
    ALREADY_MEMBER: error_type_1.default.API_ERR_ALREADY_MEMBER,
    SSO_INVALID_EXCHANGE: 'sso.invalid_exchange',
    SSO_INVALID_MFA_TOKEN: 'sso.invalid_mfa_token',
    SSO_MFA_FAILED: 'sso.mfa_failed',
    /**
     * `ssoMfaSend` asked to send a factor the account does not have. Rendered in the card
     * (with the password login as the way out) rather than toasted.
     */
    SSO_INVALID_MFA_METHOD: 'sso.invalid_mfa_method',
    /** `ssoPending` / `ssoContinueWithoutInvite`: the 10-minute mismatch entry is gone. */
    SSO_INVALID_PENDING: 'sso.invalid_pending',
    /** `ssoRegistration` / `ssoCompleteRegistration`: the 1-hour signup entry is gone. */
    SSO_INVALID_REGISTRATION: 'sso.invalid_registration',
    /** `ssoBegin` refusing a `returnTo` whose origin is not allow-listed. */
    SSO_INVALID_RETURN_TO: 'sso.invalid_return_to',
    /**
     * `authLookup`: 30 per IP and 10 per address in 5 minutes. An ordinary state of a
     * shared IP (a whole Cypress run is one), never breakage — the entry step falls
     * through to the password field instead of guessing the address is unknown.
     */
    LOOKUP_RATE_LIMITED: 'auth.lookup_rate_limited',
    /**
     * `inviteJoin` refusing because the session's address is not the invited one. Only
     * reachable on the `pendingInviteToken` path, where it should be impossible.
     */
    INVITE_EMAIL_MISMATCH: 'invite.email_mismatch',
});
const matchesExpected = (error, expected) => {
    const { type, code } = error.error;
    return expected.includes(type) || ((0, util_1.isDefined)(code) && expected.includes(code));
};
exports.matchesExpected = matchesExpected;
/**
 * Toast + Sentry for everything the caller did *not* declare as expected. A red
 * "Něco se pokazilo" toast stacked on top of an inline "wrong password" reads like a
 * second, broken thing, so the expected ones are dropped here.
 */
const processUnexpectedErrors = (errors, expected) => {
    if (!(0, util_1.isDefined)(errors))
        return;
    const unexpected = (0, util_1.isDefined)(expected)
        ? errors.filter((error) => !(0, exports.matchesExpected)(error, expected))
        : errors;
    if (unexpected.length > 0)
        (0, error_handler_1.processErrors)(unexpected);
};
exports.processUnexpectedErrors = processUnexpectedErrors;
